import React, { useState, useEffect, useRef } from 'react';

import { PlusOutlined, EditOutlined, DragOutlined, CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { Typography, Row, Col, Button, Modal, List, message as antMessage, Space } from 'antd';
import { useTranslation } from 'react-i18next';

import operatorApiFetch from '@services/operator/apiService';

import { AVAILABLE_WIDGETS, DEFAULT_DASHBOARD_CONFIG, getWidgetById } from './WidgetConfig';


const { Title } = Typography;

const DashboardOverview = () => {
  const { t } = useTranslation();
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  // 載入 Dashboard 配置
  useEffect(() => {
    const loadDashboardConfig = async () => {
      try {
        setLoading(true);
        const response = await operatorApiFetch('dashboard/settings');
        const result = await response.json();

        if (result.success && result.data.widgets) {
          setWidgets(result.data.widgets);
        } else {
          // 使用預設配置
          setWidgets(DEFAULT_DASHBOARD_CONFIG);
        }
      } catch (error) {
        console.error('[Dashboard] Load config error:', error);
        // 使用預設配置
        setWidgets(DEFAULT_DASHBOARD_CONFIG);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardConfig();
  }, []);

  // 保存 Dashboard 配置
  const saveDashboardConfig = async () => {
    try {
      setSaving(true);
      const response = await operatorApiFetch('dashboard/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ widgets }),
      });
      const result = await response.json();

      if (result.success) {
        antMessage.success(t('operator.dashboard.save_success'));
        setEditMode(false);
      } else {
        antMessage.error(result.message || t('operator.dashboard.save_error'));
      }
    } catch (error) {
      console.error('[Dashboard] Save config error:', error);
      antMessage.error(t('operator.dashboard.save_error'));
    } finally {
      setSaving(false);
    }
  };

  // 添加 Widget
  const addWidget = (widgetId) => {
    const exists = widgets.find(w => w.id === widgetId);
    if (exists) {
      antMessage.warning(t('operator.dashboard.widget_already_added'));
      return;
    }

    const newWidget = {
      id: widgetId,
      position: widgets.length,
    };
    setWidgets([...widgets, newWidget]);
    setAddModalVisible(false);
    antMessage.success(t('operator.dashboard.widget_added'));
  };

  // 移除 Widget
  const removeWidget = (widgetId) => {
    const updatedWidgets = widgets
      .filter(w => w.id !== widgetId)
      .map((w, index) => ({ ...w, position: index }));
    setWidgets(updatedWidgets);
    antMessage.success(t('operator.dashboard.widget_removed'));
  };

  // 拖曳開始
  const handleDragStart = (index) => {
    dragItem.current = index;
  };

  // 拖曳進入
  const handleDragEnter = (index) => {
    dragOverItem.current = index;
  };

  // 拖曳結束
  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    if (dragItem.current === dragOverItem.current) return;

    const newWidgets = [...widgets];
    const draggedItem = newWidgets[dragItem.current];

    // 移除拖曳項目
    newWidgets.splice(dragItem.current, 1);
    // 插入到新位置
    newWidgets.splice(dragOverItem.current, 0, draggedItem);

    // 更新 position
    const updatedWidgets = newWidgets.map((w, index) => ({ ...w, position: index }));
    setWidgets(updatedWidgets);

    dragItem.current = null;
    dragOverItem.current = null;
  };

  // 可添加的 Widgets（排除已添加的）
  const availableToAdd = AVAILABLE_WIDGETS.filter(
    available => !widgets.find(w => w.id === available.id)
  );

  // 渲染 Widget
  const renderWidget = (widgetConfig, index) => {
    const widgetDef = getWidgetById(widgetConfig.id);
    if (!widgetDef) return null;

    const WidgetComponent = widgetDef.component;
    const size = widgetDef.defaultSize;

    return (
      <Col
        key={widgetConfig.id}
        {...size}
        draggable={editMode}
        onDragStart={() => handleDragStart(index)}
        onDragEnter={() => handleDragEnter(index)}
        onDragEnd={handleDragEnd}
        onDragOver={(e) => e.preventDefault()}
        style={{
          cursor: editMode ? 'move' : 'default',
          opacity: dragItem.current === index ? 0.5 : 1,
        }}
      >
        <div style={{ position: 'relative' }}>
          {editMode && (
            <>
              <div
                style={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  zIndex: 10,
                  background: 'rgba(0, 0, 0, 0.6)',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  color: 'white',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <DragOutlined />
                {t('operator.dashboard.drag_to_reorder')}
              </div>
              <Button
                danger
                size="small"
                icon={<CloseOutlined />}
                onClick={() => removeWidget(widgetConfig.id)}
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 10,
                }}
              />
            </>
          )}
          <WidgetComponent />
        </div>
      </Col>
    );
  };

  if (loading) {
    return (
      <div className="operator-dashboard">
        <Title level={2} className="operator-page-title">
          {t('operator.dashboard.title')}
        </Title>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          {t('common.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className="operator-dashboard">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2} className="operator-page-title" style={{ margin: 0 }}>
          {t('operator.dashboard.title')}
        </Title>
        <Space>
          {!editMode ? (
            <Button
              icon={<EditOutlined />}
              onClick={() => setEditMode(true)}
            >
              {t('operator.dashboard.edit_mode')}
            </Button>
          ) : (
            <>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={saveDashboardConfig}
                loading={saving}
              >
                {t('common.button.save')}
              </Button>
              <Button
                icon={<CloseOutlined />}
                onClick={() => setEditMode(false)}
              >
                {t('common.button.cancel')}
              </Button>
            </>
          )}
        </Space>
      </div>

      {editMode && (
        <div style={{ marginBottom: 16 }}>
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => setAddModalVisible(true)}
            block
            disabled={availableToAdd.length === 0}
          >
            {t('operator.dashboard.add_widget')}
          </Button>
        </div>
      )}

      {widgets.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
          {t('operator.dashboard.no_widgets')}
          <br />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditMode(true);
              setAddModalVisible(true);
            }}
            style={{ marginTop: 16 }}
          >
            {t('operator.dashboard.add_first_widget')}
          </Button>
        </div>
      ) : (
        <Row gutter={[16, 16]}>
          {widgets
            .sort((a, b) => a.position - b.position)
            .map((widget, index) => renderWidget(widget, index))}
        </Row>
      )}

      {/* 添加 Widget Modal */}
      <Modal
        title={t('operator.dashboard.add_widget')}
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        footer={null}
        width={600}
      >
        <List
          dataSource={availableToAdd}
          renderItem={(widget) => (
            <List.Item
              actions={[
                <Button
                  key="add"
                  type="primary"
                  size="small"
                  onClick={() => addWidget(widget.id)}
                >
                  {t('common.button.add')}
                </Button>
              ]}
            >
              <List.Item.Meta
                title={t(widget.name)}
                description={t(widget.description)}
              />
            </List.Item>
          )}
          locale={{
            emptyText: t('operator.dashboard.all_widgets_added'),
          }}
        />
      </Modal>
    </div>
  );
};

export default DashboardOverview;
